<template>
  <div v-if="isReportMode">
    <ReportItemContainer>
      <template #question>{{ question.text }}</template>
      <template #answer>
        <p>{{ reportViewAnswerText }}</p>
        <p>{{ boolWithDetails.details }}</p>
      </template>
    </ReportItemContainer>
  </div>
  <div v-else>
    <BooleanQuestion
      :question-model="questionModel"
      :answer="boolWithDetails.boolean"
      :is-read-only-mode="isReadOnlyMode"
      @q-event="activate"
    />
    <div v-if="boolWithDetails.boolean === StringBoolean.Yes" class="mt-3">
      <p>{{ question?.detailsText }}</p>
      <PInputField
        :value="boolWithDetails.details"
        @update-event="(val) => emitNewBoolWithDetails(val)"
      ></PInputField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BooleanQuestion from "./BooleanQuestion.vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import { StringBoolean } from "@/models/enum/stringBoolean";
import BooleanWithDetails from "@/models/questions/booleanWithDetails";
import { computed } from "vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import PInputField from "@/components/custom/PInputField.vue";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: string | object | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newBoolean: BooleanWithDetails): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();

const boolWithDetails = ref(
  (props.answer as BooleanWithDetails) ?? new BooleanWithDetails()
);
const deactivateDetails = ref(
  !(boolWithDetails.value.boolean === StringBoolean.Yes)
);
const question = computed(() => props.questionModel.question);

const reportViewAnswerText = computed((): string =>
  selectedQuestionnaireStore.getTextForAnswerId(
    boolWithDetails.value.boolean,
    props.questionModel?.question.type!
  )
);

function activate(boolValue: string): void {
  boolWithDetails.value.boolean = boolValue;
  if (boolValue === StringBoolean.No) {
    boolWithDetails.value.details = "";
  }
  deactivateDetails.value = !(boolValue === StringBoolean.Yes);
  emitNewBoolWithDetails(boolWithDetails.value.details);
}

function emitNewBoolWithDetails(val: string): void {
  boolWithDetails.value.details = val;
  emit("qEvent", boolWithDetails.value);
}
</script>

<style lang="scss" scoped>
p {
  white-space: pre-line;
}
</style>
